import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../../lib/utils'
import { fieldGroupStatus, useFieldStatus, type FieldStatus } from './field'

/**
 * InputGroup — the shell that wraps a borderless control plus leading/trailing
 * addons (icons, buttons, selectors) as a single bordered unit. Unlike `Input`
 * (which owns its own border + focus ring), the *shell* owns the border and the
 * `focus-within` ring, so the whole row — addon, field, button — lights up
 * together. This is the foundation the `SearchBar` and future dropdown/selector
 * inputs are built on.
 *
 *   <InputGroup>
 *     <InputGroupAddon>{icon}</InputGroupAddon>
 *     <InputGroupInput placeholder="Search" />
 *     <InputGroupAddon align="trailing" divided>{button}</InputGroupAddon>
 *   </InputGroup>
 *
 * Reuses the same border + ring tokens as `Input` via `fieldGroupStatus`, so the
 * family stays visually identical. Status is inherited from a parent `<Field>`
 * (or set via the `status` prop); `size` + `disabled` are published to the
 * inner `InputGroupInput` through context.
 *
 * `overflow-hidden` clips segmented addon fills to the rounded corners; the
 * focus ring is a box-shadow so it still shows outside that clip.
 */

type InputGroupSize = 'sm' | 'md' | 'lg'

interface InputGroupContextValue {
  size: InputGroupSize
  status: FieldStatus
  disabled: boolean
}

const InputGroupContext = React.createContext<InputGroupContextValue>({
  size: 'md',
  status: 'default',
  disabled: false,
})

export const inputGroupVariants = cva(
  cn(
    'flex w-full items-center overflow-hidden rounded-lg border bg-fbui-base-white',
    'outline-none transition focus-within:ring-4',
    'has-[:disabled]:cursor-not-allowed has-[:disabled]:bg-fbui-gray-50'
  ),
  {
    variants: {
      status: fieldGroupStatus,
      size: {
        sm: 'h-8',
        md: 'h-10',
        lg: 'h-11',
      },
    },
    defaultVariants: { status: 'default', size: 'md' },
  }
)

export interface InputGroupProps
  extends
    Omit<React.HTMLAttributes<HTMLDivElement>, 'color'>,
    Omit<VariantProps<typeof inputGroupVariants>, 'status'> {
  /** Border + ring color. Inherited from a parent `<Field>` when omitted. */
  status?: FieldStatus
  /** Dim the shell + disable the inner control. */
  disabled?: boolean
}

export const InputGroup = React.forwardRef<HTMLDivElement, InputGroupProps>(
  (
    { className, status, size = 'md', disabled = false, children, ...props },
    ref
  ) => {
    const resolved = useFieldStatus(status ?? undefined)
    const ctx = React.useMemo(
      () => ({ size: size ?? 'md', status: resolved, disabled }),
      [size, resolved, disabled]
    )
    return (
      <InputGroupContext.Provider value={ctx}>
        <div
          ref={ref}
          data-disabled={disabled || undefined}
          className={cn(
            inputGroupVariants({ status: resolved, size }),
            className
          )}
          {...props}
        >
          {children}
        </div>
      </InputGroupContext.Provider>
    )
  }
)
InputGroup.displayName = 'InputGroup'

/**
 * The borderless control that fills the shell. Strips its own border/ring (the
 * shell provides them) and inherits text size + disabled from `InputGroup`.
 */
export const InputGroupInput = React.forwardRef<
  HTMLInputElement,
  Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>
>(({ className, type = 'text', disabled, ...props }, ref) => {
  const { size, disabled: groupDisabled } = React.useContext(InputGroupContext)
  return (
    <input
      ref={ref}
      type={type}
      disabled={disabled ?? groupDisabled}
      className={cn(
        'min-w-0 flex-1 border-0 bg-transparent px-3 text-fbui-gray-800 outline-none',
        'placeholder:text-fbui-gray-400 focus:ring-0',
        'disabled:cursor-not-allowed disabled:bg-transparent disabled:text-fbui-text-disabled',
        size === 'sm' ? 'text-xs' : 'text-sm',
        className
      )}
      {...props}
    />
  )
})
InputGroupInput.displayName = 'InputGroupInput'

const addonVariants = cva('flex shrink-0 items-center text-fbui-gray-400', {
  variants: {
    align: { leading: '', trailing: '' },
    /**
     * `divided` segments the addon from the field with a 1px rule and stretches
     * it full-height, flush to the shell's rounded edge (the shell has no
     * padding, so it sits at the very edge). Use for a leading selector or a
     * trailing button. `false` is a padded inline wrapper for a decorative icon.
     */
    divided: { true: 'self-stretch border-fbui-gray-300', false: 'px-3' },
  },
  compoundVariants: [
    { divided: true, align: 'leading', className: 'border-r' },
    { divided: true, align: 'trailing', className: 'border-l' },
  ],
  defaultVariants: { align: 'leading', divided: false },
})

export interface InputGroupAddonProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof addonVariants> {}

/**
 * Slot for content beside the field. Default (`divided={false}`) is an inline,
 * padded wrapper for a decorative icon. `divided` segments a button/selector
 * from the field with a divider, flush to the shell edge.
 */
export const InputGroupAddon = React.forwardRef<
  HTMLDivElement,
  InputGroupAddonProps
>(({ className, align, divided, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(addonVariants({ align, divided }), className)}
    {...props}
  />
))
InputGroupAddon.displayName = 'InputGroupAddon'
